import { Menu } from "antd";
import { SidebarMenuConfigModel } from "components/sidebar/data/models/SidebarMenuConfigModel";
import { LocalContext } from "context/AuthenticationContext";
import { ContextType, createRef, PureComponent, ReactNode } from "react";
import { NavigateFunction, NavLink } from "react-router-dom";
import { colorFormat } from "utils/format/ColorFormat";
import { FeaturedSideBarEntity } from "../domain/entities/FeatureSidebarEntity";

interface ISidebar {
  sidebarOpen: boolean;
  location: Location;
  navigate: NavigateFunction;
  setSidebarOpen: (open: boolean) => void;
}

interface ISidebarState {
  openKeys: string[];
  selectedKeys: string[];
}

export default class Sidebar extends PureComponent<ISidebar, ISidebarState> {
  static contextType = LocalContext;
  context!: ContextType<typeof LocalContext>;

  private sidebarRef = createRef<HTMLDivElement>();

  private normalizePathname(pathname: string): string {
    return pathname === "/" || pathname === "" ? "/dashboard" : pathname;
  }

  private getMenuStateFromPath(pathname: string) {
    const { menuConfig } = this.context as {
      menuConfig: SidebarMenuConfigModel;
    };
    const normalizedPathname = this.normalizePathname(pathname);
    const menus = Array.isArray(menuConfig?.menu) ? menuConfig.menu : [];

    for (const menu of menus) {
      if (menu?.path === normalizedPathname) {
        return {
          openKeys: [],
          selectedKeys: [normalizedPathname],
        };
      }

      const matchedSubMenu = menu?.sub?.find(
        (subMenu) => subMenu?.path === normalizedPathname,
      );
      if (matchedSubMenu) {
        return {
          openKeys: [menu.path],
          selectedKeys: [matchedSubMenu.path],
        };
      }
    }

    return {
      openKeys: [],
      selectedKeys: [normalizedPathname],
    };
  }

  constructor(props: ISidebar) {
    super(props);
    const effectivePathname = this.normalizePathname(
      this.props.location.pathname,
    );

    this.state = {
      openKeys: [],
      selectedKeys: [effectivePathname],
    };

    this.onClickMenuItem = this.onClickMenuItem.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.getSideNav = this.getSideNav.bind(this);
    this.handleOpenChange = this.handleOpenChange.bind(this);
  }

  private handleClickOutside(event: MouseEvent) {
    if (
      this.sidebarRef.current &&
      !this.sidebarRef.current.contains(event.target as Node)
    ) {
      this.props.setSidebarOpen(false);
    }
  }

  private handleKeyDown(event: KeyboardEvent) {
    if (!this.props.sidebarOpen || event.key !== "Escape") return;
    this.props.setSidebarOpen(false);
  }

  private getMenuItem(
    label: ReactNode,
    key: string,
    icon: ReactNode,
    children?: any,
    type?: any,
    labelClassName?: string,
  ) {
    return {
      key,
      icon,
      children,
      label: labelClassName ? (
        <span className={labelClassName}>{label}</span>
      ) : (
        label
      ),
      type,
    };
  }

  private async onClickMenuItem(e: any) {
    this.props.navigate(e.key);
    this.props.setSidebarOpen(false);
  }

  private handleOpenChange(openKeys: string[]) {
    this.setState({ openKeys });
  }

  private getSideNav() {
    const { menuConfig } = this.context as {
      menuConfig: SidebarMenuConfigModel;
    };
    const { pathname } = this.props.location;
    const menus = Array.isArray(menuConfig?.menu) ? menuConfig.menu : [];

    return menus.map((menu) => {
      return {
        item: new FeaturedSideBarEntity({
          name: menu?.name,
          code: menu?.code,
          componentName: menu?.componentName,
          path: menu?.path,
          icon: (
            <img
              src={menu?.icon as string}
              alt={menu?.name}
              style={{
                filter: this.normalizePathname(pathname).includes(menu?.path)
                  ? colorFormat?.FilterBlueImage
                  : "none",
              }}
              className="object-cover w-[17px] h-[17px]"
            />
          ),
          capabilities: menu.capabilities,
        }),
        children:
          menu.sub?.length > 0
            ? menu?.sub.map(
                (subMenu) =>
                  new FeaturedSideBarEntity({
                    name: subMenu?.name,
                    code: subMenu?.code,
                    componentName: subMenu?.componentName,
                    path: subMenu?.path,
                    icon: (
                      <img
                        src={subMenu?.icon as string}
                        alt={subMenu?.name}
                        style={{
                          filter: this.normalizePathname(pathname).includes(
                            subMenu?.path,
                          )
                            ? colorFormat?.FilterBlueImage
                            : "none",
                        }}
                        className="object-cover w-[17px] h-[17px]"
                      />
                    ),
                    capabilities: subMenu.capabilities,
                  }),
              )
            : undefined,
      };
    });
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOutside);
    document.addEventListener("keydown", this.handleKeyDown);
    const nextState = this.getMenuStateFromPath(this.props.location.pathname);
    this.setState(nextState);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  componentDidUpdate(prevProps: ISidebar) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      const nextState = this.getMenuStateFromPath(this.props.location.pathname);
      const isNeedToUpdate =
        JSON.stringify(nextState.openKeys) !==
          JSON.stringify(this.state.openKeys) ||
        JSON.stringify(nextState.selectedKeys) !==
          JSON.stringify(this.state.selectedKeys);

      if (isNeedToUpdate) {
        this.setState(nextState);
      }
    }
  }

  render(): ReactNode {
    const { sidebarOpen } = this.props;
    const { openKeys, selectedKeys } = this.state;

    const sideNav = this.getSideNav();
    const itemsNav = sideNav?.map((nav) =>
      this.getMenuItem(
        nav.item.name,
        nav.item.path,
        nav.item.icon,
        nav.children?.map((subNav) =>
          this.getMenuItem(
            subNav.name,
            subNav.path,
            subNav.icon,
            undefined,
            undefined,
            "text-[13px] leading-[18px] whitespace-nowrap",
          ),
        ),
      ),
    );

    return (
      <div className="xl:w-64 w-0">
        <div
          className={`fixed inset-0 bg-opacity-30 z-40 xl:hidden xl:z-auto transition-opacity duration-200 ${
            sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          aria-hidden="true"
        ></div>
        <div
          ref={this.sidebarRef}
          id="sidebar"
          className={`absolute xl:z-20 z-50 left-0 top-0 xl:static xl:left-auto xl:top-auto xl:translate-x-0 transform h-screen overflow-y-scroll xl:overflow-y-auto no-scrollbar w-64 flex-shrink-0 xl:bg-[#007AFF0D] bg-gray-50 px-2 py-1 transition-transform duration-200 ease-in-out ${
            sidebarOpen ? "translate-x-0" : "-translate-x-64"
          }`}
        >
          <div className="flex flex-col justify-between pr-2 sm:px-1">
            <NavLink to="/" className="block w-full">
              <div
                className="flex justify-start items-center object-contain h-[63px] px-4 border-b border-slate-200"
                style={{ backgroundColor: "transparent" }}
              >
                <span className="cube">
                  <div className="front">CMS</div>
                  <div className="back">CMS</div>
                  <div className="right">CMS</div>
                  <div className="left">CMS</div>
                  <div className="top">CMS</div>
                  <div className="bottom">CMS</div>
                </span>

                <span className="ml-3 text-sm font-semibold">B2B</span>
              </div>
            </NavLink>
          </div>
          <Menu
            mode="inline"
            items={itemsNav}
            onClick={this.onClickMenuItem}
            openKeys={openKeys}
            selectedKeys={selectedKeys}
            onOpenChange={this.handleOpenChange}
            style={{ borderInlineEnd: "none", fontWeight: 500 }}
          />
        </div>
      </div>
    );
  }
}
